import { isRedirect, redirect } from '@tanstack/react-router';

import {
    canAccessOnboardingStep,
    getOnboardingPathname,
    getResumeStep,
    type OnboardingStep
} from '@/features/onboarding/data';
import { trackCheckoutCompletedOnce } from '@/features/onboarding/analytics';
import { getOnboardingState, type OnboardingStateResponse } from '@/features/onboarding/services/onboarding';
import { onboardingKeys } from '@/features/onboarding/services/queryKeys';
import { syncOnboardingStoreFromServer, useOnboardingStore } from '@/features/onboarding/store';
import { listOrganizations } from '@/features/organizations/services/api';
import type { Organization } from '@/features/organizations/api/schemas';
import { canManageOrganization } from '@/features/organizations/permissions';
import { organizationKeys } from '@/features/organizations/services/queryKeys';
import { useCurrentOrganizationStore } from '@/features/organizations/store/useCurrentOrganizationStore';
import { ensureKeycloakInit } from '@/lib/auth-bootstrap';
import { appQueryClient } from '@/lib/query-client';
import { requireAuthenticatedRoute } from '@/lib/router-guards';
import { getAppBootstrapSnapshot } from '@/providers/app-bootstrap-snapshot';

type OnboardingRouteContext = {
    organizationId: string;
} & (
    | {
          state: 'loaded';
          server: OnboardingStateResponse;
      }
    | {
          state: 'checkout-success-retry';
          server: null;
      }
);

type OnboardingRedirectTarget = {
    to: '/onboarding';
};

type OnboardingRequirement =
    | {
          status: 'clear';
          organizationId: string | null;
          server: OnboardingStateResponse | null;
      }
    | {
          status: 'redirect';
          target: OnboardingRedirectTarget;
      }
    | {
          status: 'failed';
      };

/**
 * Resolves the organization the onboarding flow is scoped to: the persisted
 * current-organization selection when it is still valid, otherwise the first
 * organization from the (cached) list. Returns null when the user has no
 * organizations yet.
 */
function canManageOnboarding(organization: Organization): boolean {
    return canManageOrganization(organization.membership.role);
}

async function resolveOnboardingOrganization(): Promise<Organization | null> {
    const organizations = await appQueryClient.fetchQuery({
        queryKey: organizationKeys.list(),
        queryFn: listOrganizations,
        staleTime: 0,
        retry: false
    });
    const currentOrganizationStore = useCurrentOrganizationStore.getState();

    if (organizations.length === 0) {
        if (currentOrganizationStore.currentOrganizationId !== null) {
            currentOrganizationStore.setCurrentOrganizationId(null);
        }

        return null;
    }

    const storedId = currentOrganizationStore.currentOrganizationId;
    const storedOrganization = storedId
        ? organizations.find((organization) => organization.id === storedId)
        : undefined;
    if (storedOrganization) {
        return storedOrganization;
    }

    const fallbackOrganization = organizations[0];
    currentOrganizationStore.setCurrentOrganizationId(fallbackOrganization.id);

    return fallbackOrganization;
}

async function resolveOnboardingRouteContext(): Promise<OnboardingRouteContext> {
    await requireAuthenticatedRoute();

    let organization: Organization | null;
    let server: OnboardingStateResponse;

    try {
        organization = await resolveOnboardingOrganization();
    } catch (error) {
        if (isRedirect(error)) {
            throw error;
        }

        const currentOrganizationId = useCurrentOrganizationStore.getState().currentOrganizationId;
        if (isCheckoutSuccessReturn() && currentOrganizationId) {
            return {
                state: 'checkout-success-retry',
                organizationId: currentOrganizationId,
                server: null
            };
        }

        // Backend unavailable / unexpected failure: never brick navigation —
        // fall back to the organizations overview instead of the onboarding UI.
        throw redirect({ to: '/organizations' });
    }

    if (!organization) {
        throw redirect({ to: '/organizations' });
    }

    if (!canManageOnboarding(organization)) {
        throw redirect({ to: '/dashboard', search: {} as never });
    }

    const organizationId = organization.id;

    try {
        server = await appQueryClient.fetchQuery({
            queryKey: onboardingKeys.state(organizationId),
            queryFn: () => getOnboardingState(organizationId),
            staleTime: 0,
            retry: false
        });
    } catch (error) {
        if (isRedirect(error)) {
            throw error;
        }

        if (isCheckoutSuccessReturn()) {
            return {
                state: 'checkout-success-retry',
                organizationId,
                server: null
            };
        }

        // Backend unavailable / unexpected failure: never brick navigation —
        // fall back to the organizations overview instead of the onboarding UI.
        throw redirect({ to: '/organizations' });
    }

    syncOnboardingStoreFromServer(organizationId, server);

    return { state: 'loaded', organizationId, server };
}

function redirectForCompletedOnboarding(server: OnboardingStateResponse): never {
    throw redirect({
        to: '/dashboard',
        search: { welcome: '1', buildMode: server.buildMode ?? undefined } as never
    });
}

function isCheckoutSuccessReturn(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    return new URLSearchParams(window.location.search).get('checkout') === 'success';
}

export async function requireOnboardingStep(step: OnboardingStep) {
    const context = await resolveOnboardingRouteContext();

    if (context.state === 'checkout-success-retry') {
        if (step === 2) {
            return;
        }

        throw redirect({ to: '/onboarding/connect' });
    }

    const { organizationId, server } = context;

    if (server.completed) {
        if (step === 2 && isCheckoutSuccessReturn()) {
            void trackCheckoutCompletedOnce({
                organizationId,
                completedAt: server.completedAt,
                sources: server.sources
            });
        }

        redirectForCompletedOnboarding(server);
    }

    const state = useOnboardingStore.getState();

    if (!canAccessOnboardingStep(state, step)) {
        throw redirect({ to: getOnboardingPathname(getResumeStep(state)) });
    }
}

export async function redirectToOnboardingResumeStep() {
    const context = await resolveOnboardingRouteContext();

    if (context.state === 'checkout-success-retry') {
        throw redirect({ to: '/onboarding/connect' });
    }

    const { server } = context;

    if (server.completed) {
        redirectForCompletedOnboarding(server);
    }

    const state = useOnboardingStore.getState();
    throw redirect({ to: getOnboardingPathname(getResumeStep(state)) });
}

export async function getOnboardingRequirement(): Promise<OnboardingRequirement> {
    try {
        const bootstrapSnapshot = getAppBootstrapSnapshot();
        const isAuthenticated =
            bootstrapSnapshot?.status === 'ready'
                ? bootstrapSnapshot.isAuthenticated
                : await ensureKeycloakInit('check-sso');

        if (!isAuthenticated) {
            return { status: 'clear', organizationId: null, server: null };
        }

        const organization = await resolveOnboardingOrganization();

        if (!organization) {
            return { status: 'clear', organizationId: null, server: null };
        }

        const organizationId = organization.id;

        const server = await appQueryClient.fetchQuery({
            queryKey: onboardingKeys.state(organizationId),
            queryFn: () => getOnboardingState(organizationId),
            staleTime: 0,
            retry: false
        });

        if (server.shouldShow && !server.completed) {
            if (!canManageOnboarding(organization)) {
                return { status: 'clear', organizationId, server };
            }

            syncOnboardingStoreFromServer(organizationId, server);
            return { status: 'redirect', target: { to: '/onboarding' } };
        }

        return { status: 'clear', organizationId, server };
    } catch {
        return { status: 'failed' };
    }
}

/**
 * Shared app gate: routes authenticated users with an organization into
 * onboarding while the backend says it should be shown. Silently returns null
 * on any failure so callers can fall through to their default route.
 */
export async function getRequiredOnboardingRedirect(): Promise<OnboardingRedirectTarget | null> {
    const requirement = await getOnboardingRequirement();

    return requirement.status === 'redirect' ? requirement.target : null;
}

export async function redirectToOnboardingWhenRequired(): Promise<void> {
    const redirectTarget = await getRequiredOnboardingRedirect();

    if (redirectTarget) {
        throw redirect(redirectTarget);
    }
}
